import { Link, useLocation } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { ReactNode } from "react";

/**
 * Top-of-page breadcrumbs.
 *
 * Path-based resolution — walk pathname segments and emit a crumb per
 * segment. Supports the 3-level hedge hierarchy
 *   / -> /pairs/:slug -> /pairs/:slug/:symbol
 * plus flat routes (/accounts, /accounts/:id, etc.) and the catch-all
 * "Dashboard" fallback.
 *
 * Every crumb (except the final "current page") uses React Router
 * <Link> so SPA navigation doesn't full-page-reload.
 */

const STATIC_LABELS: Record<string, string> = {
  "/": "Dashboard",
  "/overview": "Overview",
  "/accounts": "Accounts",
  "/decisions": "Decisions",
  "/compound": "Compound",
  "/firms": "Firms",
  "/alerts": "Alerts",
  "/settings": "Settings",
  "/pairs": "Account Pairs",
  "/terminal": "FX Terminal",
  "/futures-terminal": "Futures Terminal",
  "/futures": "Futures",
  "/futures/accounts": "Futures Accounts",
  "/futures/partner-sets": "Futures Partner Sets",
  "/futures/terminal": "Futures Terminal",
  "/futures/platforms": "Futures Platforms",
  "/futures/risk": "Futures Risk",
  "/futures/bots": "Futures Bots",
};

interface Crumb {
  label: string;
  href?: string;
}

function buildCrumbs(pathname: string): Crumb[] {
  // Root → single "Dashboard" crumb (no link needed — we're already here).
  if (pathname === "/") return [{ label: "Dashboard" }];

  const crumbs: Crumb[] = [{ label: "Dashboard", href: "/" }];

  const segments = pathname.split("/").filter(Boolean);

  // Hedge hierarchy: /pairs/:slug[/:symbol]
  if (segments[0] === "pairs") {
    crumbs.push({
      label: "Account Pairs",
      href: segments.length > 1 ? "/pairs" : undefined,
    });
    if (segments.length >= 2) {
      // Level 2 crumb — use the opaque slug. The page itself renders
      // the firm ↔ firm headline, so a short slug here is fine.
      const slug = segments[1] ?? "";
      crumbs.push({
        label: `Pair ${slug.slice(0, 8)}`,
        href: segments.length > 2 ? `/pairs/${slug}` : undefined,
      });
    }
    if (segments.length >= 3) {
      crumbs.push({ label: (segments[2] ?? "").toUpperCase() });
    }
    return crumbs;
  }

  // /accounts[/:id]
  if (segments[0] === "accounts") {
    crumbs.push({
      label: "Accounts",
      href: segments.length > 1 ? "/accounts" : undefined,
    });
    if (segments.length >= 2) {
      const id = segments[1] ?? "";
      crumbs.push({ label: `Account ${id.slice(0, 8)}` });
    }
    return crumbs;
  }

  // /futures/...
  if (segments[0] === "futures") {
    crumbs.push({
      label: "Futures",
      href: segments.length > 1 ? "/futures/accounts" : undefined,
    });
    if (segments.length >= 2) {
      const section = segments[1] ?? "";
      const sectionHref = `/futures/${section}`;
      crumbs.push({
        label: STATIC_LABELS[sectionHref] ?? section.charAt(0).toUpperCase() + section.slice(1),
        href: segments.length > 2 ? sectionHref : undefined,
      });
    }
    if (segments.length >= 3) {
      const slug = segments[2] ?? "";
      crumbs.push({ label: `Pair ${slug.slice(0, 8)}` });
    }
    return crumbs;
  }

  // /terminal[/:slug] and legacy /futures-terminal[/:slug]
  if (segments[0] === "terminal" || segments[0] === "futures-terminal") {
    const base =
      segments[0] === "futures-terminal" ? "/futures-terminal" : "/terminal";
    crumbs.push({
      label: segments[0] === "futures-terminal" ? "Futures Terminal" : "FX Terminal",
      href: segments.length > 1 ? base : undefined,
    });
    if (segments.length >= 2) {
      const slug = segments[1] ?? "";
      crumbs.push({ label: `Pair ${slug.slice(0, 8)}` });
    }
    return crumbs;
  }

  // Flat known routes
  const label = STATIC_LABELS[pathname];
  if (label) {
    crumbs.push({ label });
    return crumbs;
  }

  // Unknown — show raw final segment in Title Case.
  const last = segments[segments.length - 1] ?? "";
  crumbs.push({
    label: last.charAt(0).toUpperCase() + last.slice(1),
  });
  return crumbs;
}

export function TopBar(): ReactNode {
  const { pathname } = useLocation();
  const crumbs = buildCrumbs(pathname);

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3 font-sans sm:px-4">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 !h-4" />

      <Breadcrumb className="flex-1 min-w-0">
        <BreadcrumbList>
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <span key={`${i}-${c.label}`} className="flex items-center gap-1.5">
                {i > 0 && (
                  <BreadcrumbSeparator className={i === 1 ? "hidden sm:block" : ""} />
                )}
                <BreadcrumbItem
                  className={i === 0 ? "hidden sm:block" : undefined}
                >
                  {isLast || !c.href ? (
                    <BreadcrumbPage className="truncate">
                      {c.label}
                    </BreadcrumbPage>
                  ) : (
                    // Base UI's BreadcrumbLink accepts `render` to swap
                    // the underlying element — we swap to React Router's
                    // <Link> so navigation stays SPA.
                    <BreadcrumbLink render={<Link to={c.href} />}>
                      {c.label}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </span>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    </header>
  );
}
